/**
 * Card — composable surface with optional header, footer, and accent.
 *
 * Single responsibility: provide a consistent rectangular surface that
 * other components compose around. No domain knowledge, no internal state.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface CardProps {
  /** Optional header rendered above the body with a divider. */
  readonly header?: React.ReactNode;
  /** Optional footer rendered below the body with a divider. */
  readonly footer?: React.ReactNode;
  /** Body content. */
  readonly children?: React.ReactNode;
  /** Accent color rendered as a left border stripe. */
  readonly accent?: string;
  /** Disable the default body padding (useful when embedding tables). */
  readonly flush?: boolean;
  /** Optional inline style overrides on the outer wrapper. */
  readonly style?: React.CSSProperties;
  /** Optional id for in-page anchoring. */
  readonly id?: string;
  /** Optional aria-label for assistive technology. */
  readonly "aria-label"?: string;
  /** Optional aria-describedby pointing at a sibling describing this card. */
  readonly "aria-describedby"?: string;
  /** Additional Tailwind class names appended to the outer wrapper. */
  readonly className?: string;
}

const HEADER_STYLE: React.CSSProperties = {
  padding: "0.875rem 1rem",
  borderBottom: `1px solid ${GRAY[300]}`,
  fontWeight: 600,
  color: GRAY[900],
  fontSize: "0.9375rem",
};

const FOOTER_STYLE: React.CSSProperties = {
  padding: "0.75rem 1rem",
  borderTop: `1px solid ${GRAY[300]}`,
  fontSize: "0.8125rem",
  color: GRAY[600],
  backgroundColor: "var(--csk-elevated, #f1f5f9)",
};

/** Renders a bordered surface with optional header/footer slots. */
export const Card = React.memo(function Card({
  header,
  footer,
  children,
  accent,
  flush = false,
  style,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  className,
}: CardProps): React.JSX.Element {
  const wrapper: React.CSSProperties = {
    backgroundColor: "white",
    border: `1px solid ${GRAY[300]}`,
    borderLeft: accent !== undefined ? `4px solid ${accent}` : `1px solid ${GRAY[300]}`,
    borderRadius: "0.5rem",
    overflow: "hidden",
    // Stack header / body / footer in a column so a footer pins to the
    // bottom and the body can grow to fill an equal-height card (e.g. when
    // the wrapper is given `h-full` inside a stretched grid row).
    display: "flex",
    flexDirection: "column",
    ...style,
  };

  return (
    <section
      style={wrapper}
      id={id}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={cn("font-body", className)}
    >
      {header !== undefined && <div style={HEADER_STYLE}>{header}</div>}
      <div style={{ padding: flush ? 0 : "1rem", flex: "1 1 auto" }}>{children}</div>
      {footer !== undefined && <div style={FOOTER_STYLE}>{footer}</div>}
    </section>
  );
});
